import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '../api/clients.api'
import { ErrorMessage } from './ErrorMessage'
import type { ClientCategory, Client } from '../api/types'
import { useAuthStore } from '../../modules/auth/authStore'

interface Props {
  onCreated: (client: Client) => void
  onClose: () => void
}

export function NewClientModal({ onCreated, onClose }: Props) {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  // Роль ASSISTANT (Помощник): клиент создаётся для одного из прикреплённых менеджеров.
  const isAssistant = user?.role === 'ASSISTANT'
  const assistantManagers = user?.assistantManagers ?? []
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [category, setCategory] = useState<ClientCategory>('C')
  const [notes, setNotes] = useState('')
  const [ownerManagerId, setOwnerManagerId] = useState(
    isAssistant ? (assistantManagers[0]?.id ?? '') : '',
  )
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    nameRef.current?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const mutation = useMutation({
    mutationFn: () =>
      createClient({
        name: name.trim(),
        code: code.trim() || undefined,
        category,
        notes: notes.trim() || undefined,
        ...(isAssistant && ownerManagerId ? { managerId: ownerManagerId } : {}),
      }),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      onCreated(client)
    },
  })

  const canSubmit =
    name.trim().length >= 2 && !mutation.isPending && (!isAssistant || !!ownerManagerId)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md mx-4 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Новый клиент</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {isAssistant && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Клиент принадлежит менеджеру <span className="text-red-500">*</span>
            </label>
            <select
              value={ownerManagerId}
              onChange={(e) => setOwnerManagerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              <option value="" disabled>
                Выберите менеджера…
              </option>
              {assistantManagers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name || m.email}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Название <span className="text-red-500">*</span>
          </label>
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) mutation.mutate()
            }}
            placeholder="ООО Рога и Копыта"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Код клиента</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ROG001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Категория</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ClientCategory)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              <option value="A">A — Ключевой заказчик</option>
              <option value="B">B — Растущий клиент</option>
              <option value="C">C — Новый заказчик</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Заметки</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Необязательно..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />
        </div>

        {mutation.isError && <ErrorMessage error={mutation.error} />}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="px-5 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Создание...' : 'Создать клиента'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  )
}
